/**
 * useLessonEngine
 *
 * The React hook that powers all lesson progression logic.
 * Wraps the pure lessonReducer with React state, auto-save, and exposes
 * a clean API for Lesson.jsx and its sub-components.
 *
 * HOW TO USE IN SHADOW MODE (before cutting over):
 *
 *   const engine = useLessonEngine(CourseTheatreRecipe, user.userProgress, updateUser)
 *
 *   // Compare old and new in a useEffect:
 *   useEffect(() => {
 *     console.log('[SHADOW] OLD cursor:', [CourseProgIndex, LessonProgIndex, StepProgIndex, StepItemProgIndex])
 *     console.log('[SHADOW] NEW activeStepItemId:', engine.activeStepItemId)
 *     console.log('[SHADOW] NEW progressById:', engine.progressById)
 *   }, [engine.progressById])
 *
 * Keep old code running until logs agree, then cut over.
 */

import { useReducer, useEffect, useRef, useMemo } from 'react';
import { buildRecipeIndex, getStepAccessState, resolveViewingStepId } from '../utils/buildRecipeIndex';
import { migrateProgress } from '../utils/migrateProgress';
import {
  lessonReducer,
  createInitialState,
  selectViewingStepId,
  selectActiveCelebration,
  selectIsCourseCompleted,
  selectIsCurrentLessonCompleted,
  selectCurrentLessonNode,
  selectIsActiveItemCompleted,
  selectSavePayload,
} from '../utils/lessonReducer';


function useLessonEngine(courseRecipe, storedProgress, saveToDb) {
  // ---------------------------------------------------------------------------
  // Build recipe index once (never changes at runtime)
  // ---------------------------------------------------------------------------
  const idx = useMemo(() => buildRecipeIndex(courseRecipe), [courseRecipe]);

  // ---------------------------------------------------------------------------
  // Migrate stored progress → new format, derive initial state
  // ---------------------------------------------------------------------------
  const initialState = useMemo(() => {
    const migrated = migrateProgress(idx, storedProgress);
    return createInitialState(migrated);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally empty deps — we only want to run this once at mount.
  // After that, the reducer owns all state.

  // ---------------------------------------------------------------------------
  // Reducer
  // ---------------------------------------------------------------------------
  const [state, dispatch] = useReducer(
    (s, action) => lessonReducer(idx, s, action),
    initialState
  );

  // ---------------------------------------------------------------------------
  // Auto-save
  //
  // Fires whenever saveRevision changes or lastSavedRevision changes.
  // The lastSavedRevision dep ensures that if a save comes in while another
  // is in-flight, we retry after the first one settles.
  // ---------------------------------------------------------------------------
  const inflightRef = useRef(false);

  useEffect(() => {
    if (state.saveRevision <= state.lastSavedRevision) return;
    if (inflightRef.current) return;
    if (!saveToDb) return;

    inflightRef.current = true;
    const revision = state.saveRevision;
    const payload = selectSavePayload(state);

    saveToDb(payload)
      .then(() => dispatch({ type: 'SAVE_OK', revision }))
      .catch((err) => {
        console.error('[useLessonEngine] Save failed:', err);
        dispatch({ type: 'SAVE_FAIL', revision });
      })
      .finally(() => {
        inflightRef.current = false;
      });
  }, [state.saveRevision, state.lastSavedRevision, state.progressById, state.activeStepItemId]);
  // Note: state.progressById and state.activeStepItemId included so the latest
  // payload is always used if a retry fires after a failed save.

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------
  const activeCelebration    = selectActiveCelebration(state);
  const isCourseCompleted    = selectIsCourseCompleted(idx, state);
  const isLessonCompleted    = selectIsCurrentLessonCompleted(idx, state);
  const currentLessonNode    = selectCurrentLessonNode(idx, state);
  const isActiveItemCompleted = selectIsActiveItemCompleted(state);
  const viewingStepId        = selectViewingStepId(idx, state);

  const activeStepId   = idx.parentOf[state.activeStepItemId] ?? null;
  const activeLessonId = activeStepId ? idx.parentOf[activeStepId] : null;

  const isSingleStepLesson = currentLessonNode?.type === 'SingleStepLesson';

  const lessonHasStarted = useMemo(() => {
    if (!activeLessonId) return false;
    const lessonStepIds = idx.childrenOf[activeLessonId] || [];
    return lessonStepIds.some((stepId) => {
      const stepItemIds = idx.childrenOf[stepId] || [];
      return stepItemIds.some((siId) => state.progressById[siId]?.completed && !idx.isNotMandatory[siId]);
    });
  }, [state.progressById, activeLessonId, idx]);

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------
  return {
    // --- State (read-only) ---
    progressById:        state.progressById,
    activeStepItemId:    state.activeStepItemId,
    viewingStepId,       // resolved: always a stepId or null
    viewingStepExplicit: !!state.viewingStepId, // true only when user explicitly browsed to a step

    celebrationQueue:    state.celebrationQueue,
    activeCelebration,   // first item in queue, or null

    // --- Completion flags ---
    isCourseCompleted,
    isLessonCompleted,
    isActiveItemCompleted,
    isSingleStepLesson,
    lessonHasStarted,

    // --- Recipe data for rendering ---
    idx,
    activeStepNode:     idx.nodeOf[state.activeStepItemId] ?? null,
    activeStepItemNode: idx.nodeOf[state.activeStepItemId] ?? null, // alias for clarity
    viewingStepNode:    viewingStepId ? idx.nodeOf[viewingStepId] : null,
    currentLessonNode,

    // --- Step access state (for LessonPanel grid) ---
    // Call this per step: engine.getStepState("puppet_intro") → "completed" | "active" | "locked"
    getStepState: (stepId) => getStepAccessState(idx, state.progressById, stepId),

    // --- Actions ---
    onContinue:        () => dispatch({ type: 'CONTINUE' }),
    onBack:            () => dispatch({ type: 'BACK' }),
    onCelebrationDone: () => dispatch({ type: 'CELEBRATION_DONE' }),
    onBrowseStep:      (stepId) => dispatch({ type: 'BROWSE_STEP', stepId }),
    onNavigateToStep:  (stepId) => dispatch({ type: 'NAVIGATE_TO_STEP', stepId }),
    onNavigateToStepItem: (stepItemId) => dispatch({ type: 'NAVIGATE_TO_STEP_ITEM', stepItemId }),
    onMarkItemComplete:(stepItemId) => dispatch({ type: 'MARK_ITEM_COMPLETE', stepItemId }),

    // --- Dev / CHEATS ---
    devOverrideProgress: (progressById, activeStepItemId) =>
      dispatch({ type: 'DEV_OVERRIDE_PROGRESS', progressById, activeStepItemId }),
    devDispatch: dispatch, // escape hatch for CHEATS panel
  };
}


export default useLessonEngine;
